import { singleton, inject } from "tsyringe";
import { BackendClient } from "../../../connectivity/backend-client";
import type { ViewportQueryRequest, ViewportQueryResponse } from "../../../connectivity/viewport-query.types";

/**
 * A contiguous range of scheduled working days.
 */
export interface ScheduledRange {
    /** ISO date string, inclusive (e.g. "2026-04-06") */
    start: string;
    /** ISO date string, exclusive (e.g. "2026-04-10") */
    end: string;
}

/**
 * Backend DTO for resource planning schedule data.
 * Replaces the old full-entity serialization with SID-based lightweight items.
 */
export interface ResourcePlanningScheduleDto {
    issueSid: string;
    employeeSid: string;
    scheduledRanges: ScheduledRange[];
    isFixed: boolean;
    isOverdue: boolean;
    timeBudgetRemainingAtSlot: number;
    /** The assignee's own time estimate for this issue, in minutes
     *  (`EmployeeToIssueAssignment.timeBudget` for this employee) — computed
     *  server-side so the badge doesn't depend on the frontend's Issue entity
     *  cache having `employeeToIssueAssignments` hydrated (Review 0037-01,
     *  Linse A, MAJOR). */
    timeBudget: number;
}

/**
 * API client for the resources-viewport-query endpoint.
 * Returns SID-based schedule DTOs instead of full entity serializations.
 */
@singleton()
export class ResourcesViewportQueryClient {

    private static readonly ENDPOINT = '/api/v1/resources-viewport-query';

    constructor(
        @inject(BackendClient) private readonly backendClient: BackendClient
    ) {}

    /**
     * Fetches resource planning schedule data for a given viewport configuration.
     *
     * @param request - The viewport query request parameters
     * @param signal - Optional AbortSignal for request cancellation
     * @returns Promise resolving to the viewport query response with schedule DTOs
     * @throws Error if the request fails or returns invalid data
     */
    public async query(
        request: ViewportQueryRequest,
        signal?: AbortSignal
    ): Promise<ViewportQueryResponse<ResourcePlanningScheduleDto>> {
        const response = await this.backendClient.post<ViewportQueryResponse<ResourcePlanningScheduleDto>>(
            ResourcesViewportQueryClient.ENDPOINT,
            true,
            {
                xAxis: request.xAxis,
                yAxis: request.yAxis
            },
            signal
        );

        if (!response) {
            throw new Error(`Resources viewport query request failed: No response received`);
        }

        return response;
    }
}
